import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { CinematicVisual } from './CinematicVisual';
import { PeerComparisonCard } from './PeerComparisonCard';
import type { Chapter, ChapterAnswer } from '../types/pulse';
interface RewardScreenProps {
  chapter: Chapter;
  answer: ChapterAnswer | undefined;
  peerGroup: string;
  isLast: boolean;
  onContinue: () => void;
}
export function RewardScreen({
  chapter,
  answer,
  peerGroup,
  isLast,
  onContinue
}: RewardScreenProps) {
  const mainId = answer?.main ?? null;
  const followUp = mainId ? chapter.followUps[mainId] : undefined;
  const followOption = followUp?.options.find(o => o.id === answer?.followUp);
  return <div className="relative h-full bg-mist-50" data-id="element-717" data-mp="193fj8lmk3e12">
      <div className="h-full overflow-y-auto pb-28 no-scrollbar" data-id="element-718" data-mp="2yhkp02ay5u0d">
        <CinematicVisual image={chapter.image} visual={chapter.visual} accent={chapter.accent} caption={chapter.visualCaption} data-id="element-719" data-mp="2yi3yqvqg1c3b" />

        <div className="relative -mt-8 rounded-t-[28px] bg-mist-50 px-5 pt-6" data-id="element-720" data-mp="3uupzxvcem40r">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-721" data-mp="3px6h0i4rh32h">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{
            color: chapter.accent
          }} data-id="element-722" data-mp="3h28uy1aq8z7">
              Chapter {chapter.numberWord} · Your reward
            </p>
            <h2 className="mt-2 font-serif text-[25px] font-normal leading-tight text-navy-900" data-id="element-723" data-mp="3byqbuvmjrkmg">
              {chapter.rewardHeadline}
            </h2>
          </motion.div>

          <motion.div className="mt-5" initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.2
        }} data-id="element-724" data-mp="3kyv9ie1xv4nf">
            {mainId ? <PeerComparisonCard options={chapter.options} selectedId={mainId} accent={chapter.accent} peerGroup={peerGroup} data-id="element-725" data-mp="v89t3u4cw8sh" /> : <p className="rounded-3xl border border-navy-900/10 bg-white p-5 text-sm text-slate-500" data-id="element-726" data-mp="3tyjemp7tptwq">
                Answer the chapter question to see how you compare.
              </p>}
          </motion.div>

          {followOption && <motion.section aria-label="Your reasoning" initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.45
        }} className="mt-3 rounded-3xl border border-navy-900/[0.08] bg-white p-5" data-id="element-727" data-mp="6s8wor0uxsis">
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400" data-id="element-728" data-mp="3k0v6mxl7ipwm">Your reasoning</p>
              <p className="mt-1 text-[15px] leading-snug text-navy-900" data-id="element-729" data-mp="2mm0ronr39pty">{followOption.label}</p>
              <div className="mt-3 flex items-center gap-3" data-id="element-730" data-mp="2rkkgqx0zbhqj">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-mist-200" data-id="element-731" data-mp="2rj12fbyvvgre">
                  <motion.div className="h-full rounded-full" style={{
                backgroundColor: chapter.accent
              }} initial={{
                width: 0
              }} animate={{
                width: `${followOption.peerPercent}%`
              }} transition={{
                duration: 1.2,
                delay: 0.8,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-732" data-mp="2k0t4hobb26kl" />
                </div>
                <span className="text-[13px] font-semibold tabular-nums" style={{
              color: chapter.accent
            }} data-id="element-733" data-mp="2138lgxv8227w">
                  {followOption.peerPercent}%
                </span>
              </div>
              <p className="mt-2 text-[12px] leading-relaxed text-slate-500" data-id="element-734" data-mp="1rdffccdumfb1">
                of {peerGroup} who share your view gave the same reason <span className="text-slate-400" data-id="element-735" data-mp="1mbpzmjfoldgm">(illustrative)</span>
              </p>
            </motion.section>}

          <motion.blockquote initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.7
        }} className="mt-8 border-l-2 pl-4" style={{
          borderColor: chapter.accent
        }} data-id="element-736" data-mp="1dd4huqi1a2gw">
            <p className="font-serif text-[21px] font-light italic leading-snug text-navy-900" data-id="element-737" data-mp="3hittxpzt4xjx">{chapter.insight}</p>
            <footer className="mt-2 text-[10px] uppercase tracking-[0.16em] text-slate-400" data-id="element-738" data-mp="38ofbxfnw78x4">Editorial observation</footer>
          </motion.blockquote>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 border-t border-navy-900/5 bg-white/90 p-5 backdrop-blur-md" data-id="element-739" data-mp="2tt95wsosra3s">
        <button type="button" onClick={onContinue} className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-navy-900 text-[15px] font-semibold text-white transition hover:bg-navy-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2" data-id="element-740" data-mp="2ynv9wasju0du">
          {isLast ? 'See your IVF Pulse' : 'Continue the story'}
          <ArrowRightIcon className="h-4 w-4" style={{
          color: chapter.accent
        }} data-id="element-741" data-mp="nxeuh65hclew" />
        </button>
      </div>
    </div>;
}